<script setup lang="ts">
import { Wrench } from '@lucide/vue'
import {
    COMPUTER_USE_ACTIONS,
    computerUseToolAs,
    inBuiltInNamespace,
    type MustGateEntry,
    mustGateEntryTool,
} from '@zerohuman/shared'
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    ZhAddTile,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type Member, type ToolBindingRow } from '@/api'
import ListSearchField from '@/components/ListSearchField.vue'

const props = defineProps<{
    modelValue: string
    /**
     * The teams that inherit the task (`TaskRow.teams`): a run reaches the team bindings of whoever runs it, and a
     * task belongs to a role, not a team. The teams its role is held on are added once the role is read.
     */
    teamIds?: readonly string[]
    roleId?: string
    taskId?: string
    memberId?: string
    /**
     * The chosen tools marked optional, one per line (`v-model:optional`): a run goes ahead without one that is not
     * connected. Left unbound, the picker offers no Optional switch.
     */
    optional?: string
}>()
/**
 * `require-gates` names the gates a picked tool needs: a tool name, or `{ tool, match }` when its binding marks only
 * the calls carrying `match` must-gate, so the gate the form adds is narrowed the same way.
 */
const emit = defineEmits<{
    'update:modelValue': [string]
    'update:optional': [string]
    'require-gates': [MustGateEntry[]]
}>()
/**
 * `selected` draws under the chosen tools, given them in the order their cards show and the name each card shows, so
 * whatever a screen lists per tool (Create task's approvals) names a tool the way the picker does.
 */
defineSlots<{ selected?: (props: { tools: string[]; label: (name: string) => string }) => unknown }>()
/** How the picker names a tool, for a screen that names it outside the slot too (Create task's save preview, #1756). */
defineExpose({ labelFor: (name: string) => labelFor(name) })
const open = ref(false)
const search = ref('')
const loading = ref(false)
const error = ref('')
const bindings = ref<ToolBindingRow[]>([])
const holders = ref<Member[]>([])
/** The teams the chosen role is held on (`RoleDetail.teams`): a team binding there reaches a run of the task. */
const roleTeamIds = ref<string[]>([])
const reachableTeams = computed(() => new Set([...(props.teamIds ?? []), ...roleTeamIds.value]))
const builtIns = ref<Array<{ name: string; summary: string; effect: string; gate?: string; requires?: string[] }>>([])
const catalogs = ref<Record<string, Array<{ name: string; summary: string }>>>({})
const catalogErrors = ref<Record<string, string>>({})
const pending = ref<Record<string, boolean>>({})
const selected = computed(() =>
    props.modelValue
        .split('\n')
        .map(name => name.trim())
        .filter(Boolean),
)
const optionalTools = computed(() =>
    (props.optional ?? '')
        .split('\n')
        .map(name => name.trim())
        .filter(Boolean),
)
/** A built-in tool needs no connection, so it is never skipped and offers no Optional switch. */
function canBeOptional(name: string) {
    return props.optional !== undefined && !inBuiltInNamespace(name)
}
function setOptional(name: string, on: boolean | 'indeterminate') {
    const rest = optionalTools.value.filter(item => item !== name)
    emit('update:optional', (on === true ? [...rest, name] : rest).join('\n'))
}
const available = computed(() => {
    const rows = bindings.value.filter(
        row =>
            row.kind !== 'web-credential' &&
            (row.layer === 'enterprise' ||
                (row.layer === 'team' && reachableTeams.value.has(row.layerId)) ||
                (row.layer === 'role' &&
                    (row.layerId === props.roleId ||
                        holders.value.some(member => member.roles.some(role => role.id === row.layerId)))) ||
                (row.layer === 'task' && row.layerId === props.taskId) ||
                (row.layer === 'member' &&
                    (row.layerId === props.memberId || holders.value.some(member => member.id === row.layerId)))),
    )
    const rank = { enterprise: 0, team: 1, role: 3, member: 2, task: 4 }
    const nearest = new Map<string, ToolBindingRow>()
    for (const row of rows) {
        const previous = nearest.get(row.toolType)
        if (!previous || rank[row.layer] > rank[previous.layer]) nearest.set(row.toolType, row)
    }
    return [...nearest.values()].sort((a, b) => a.displayName.localeCompare(b.displayName))
})
const matches = (text: string) => text.toLowerCase().includes(search.value.trim().toLowerCase())
const visibleBindings = computed(() =>
    available.value.filter(
        row =>
            matches(`${row.displayName} ${row.toolType}`) ||
            (catalogs.value[row.id] ?? []).some(tool => matches(`${tool.name} ${tool.summary}`)),
    ),
)
const visibleBuiltIns = computed(() => builtIns.value.filter(tool => matches(`${tool.name} ${tool.summary}`)))
function bindingFor(name: string) {
    return available.value.find(row => row.toolType === name.split(/[.#]/)[0])
}
function labelFor(name: string) {
    const binding = bindingFor(name)
    return binding && !name.includes('.') ? binding.displayName : name
}
function isToolset(name: string) {
    return !name.includes('.') || name.endsWith('.*')
}
function has(name: string) {
    if (bindingFor(name)?.kind === 'computer-use' && !name.includes('.'))
        return COMPUTER_USE_ACTIONS.every(action => selected.value.includes(`${name}.${action}`))
    return selected.value.includes(name) || (!name.includes('.') && selected.value.includes(`${name}.*`))
}
function add(name: string, requires: string[] = []) {
    const binding = bindingFor(name)
    const additions =
        binding?.kind === 'computer-use' && name === binding.toolType
            ? COMPUTER_USE_ACTIONS.map(action => `${name}.${action}`)
            : [name]
    const added = [...requires, ...additions]
    const gates: MustGateEntry[] = (binding?.mustGate ?? []).filter(
        entry => name === binding?.toolType || added.includes(mustGateEntryTool(entry)),
    )
    for (const tool of builtIns.value) {
        if (added.includes(tool.name) && tool.gate) gates.push(tool.gate)
    }
    if (gates.length > 0) {
        const once = new Map(gates.map(entry => [JSON.stringify(entry), entry]))
        emit('require-gates', [...once.values()])
    }
    emit('update:modelValue', [...new Set([...selected.value, ...added])].join('\n'))
}
function remove(name: string) {
    emit('update:modelValue', selected.value.filter(item => item !== name).join('\n'))
    if (optionalTools.value.includes(name)) setOptional(name, false)
}
async function load() {
    loading.value = true
    error.value = ''
    const results = await Promise.allSettled([api.toolBindings(), api.builtInTaskTools()])
    if (results[0].status === 'fulfilled') bindings.value = results[0].value
    if (results[1].status === 'fulfilled') builtIns.value = results[1].value
    if (results.some(result => result.status === 'rejected')) error.value = 'Some tools could not be loaded. Try again.'
    loading.value = false
}
async function loadCatalog(row: ToolBindingRow) {
    if (row.kind === 'computer-use') {
        catalogs.value[row.id] = COMPUTER_USE_ACTIONS.map(action => ({
            name: `${row.toolType}.${action}`,
            summary: computerUseToolAs(`${row.toolType}.${action}`)?.summary ?? '',
        }))
        return
    }
    pending.value[row.id] = true
    delete catalogErrors.value[row.id]
    try {
        catalogs.value[row.id] = await api.toolCatalog(row.id)
        if (!catalogs.value[row.id]?.length)
            catalogErrors.value[row.id] =
                'This connection returned no actions. Reconnect with permissions, or check the server’s tool settings.'
    } catch (caught) {
        const reason = caught instanceof Error ? caught.message : ''
        const messages: Record<string, string> = {
            vendor_catalog_auth_required:
                'Authentication was rejected. Reconnect this tool in Tools and choose its permissions.',
            vendor_catalog_invalid: 'The server returned an invalid catalog. Check the server or retry.',
        }
        catalogErrors.value[row.id] = messages[reason] ?? 'Actions could not be loaded. Check the connection or retry.'
    } finally {
        pending.value[row.id] = false
    }
}
watch(open, value => {
    if (value) {
        search.value = ''
        void load()
    }
})
watch(
    () => props.roleId,
    async roleId => {
        holders.value = []
        roleTeamIds.value = []
        if (!roleId) return
        try {
            const role = await api.role(roleId)
            if (props.roleId !== roleId) return
            holders.value = role.members
            roleTeamIds.value = (role.teams ?? []).map(team => team.id)
        } catch {
            /* Shared team and enterprise connections remain available. */
        }
    },
    { immediate: true },
)
// Logos are also available on selected cards before the picker is opened.
onMounted(async () => {
    try {
        bindings.value = await api.toolBindings()
    } catch {
        /* Picker loading presents the retry. */
    }
})
</script>

<template>
    <Dialog v-model:open="open">
    <fieldset class="space-y-3" data-testid="task-tools-picker">
        <legend class="text-sm font-medium">Tools</legend>
        <p class="text-muted-foreground text-sm">Choose a toolset and let the agent pick its actions, or choose individual actions.</p>
        <div class="grid gap-3 sm:grid-cols-3">
            <Card v-for="name in selected" :key="name" data-testid="selected-tool">
                <CardContent class="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
                    <img v-if="bindingFor(name)?.imageUrl" :src="bindingFor(name)?.imageUrl" alt="" class="size-10 object-contain" />
                    <Wrench v-else aria-hidden="true" class="size-8 text-muted-foreground" />
                    <span class="max-w-full break-all text-sm font-medium">{{ labelFor(name) }}</span>
                    <span class="text-muted-foreground text-xs">{{ isToolset(name) ? 'Whole toolset' : 'Action' }}</span>
                    <label v-if="canBeOptional(name)" class="flex items-center gap-2 text-xs" :title="'Runs go ahead without this tool when it is not connected, and use it when it is.'">
                        <Checkbox :model-value="optionalTools.includes(name)" :aria-label="`${labelFor(name)} is optional`" data-testid="selected-tool-optional" @update:model-value="setOptional(name, $event)" />Optional
                    </label>
                    <Button type="button" size="sm" variant="ghost" :aria-label="`Remove ${labelFor(name)}`" @click="remove(name)">Remove</Button>
                </CardContent>
            </Card>
            <DialogTrigger as-child>
                <ZhAddTile label="Add Tool" data-testid="add-tool" @click="open = true" />
            </DialogTrigger>
        </div>
        <slot name="selected" :tools="selected" :label="labelFor" />
            <DialogContent class="max-h-screen overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Add tools</DialogTitle>
                    <DialogDescription>Choose from the connections available to this task and the built-in tools.</DialogDescription>
                </DialogHeader>
                <ListSearchField v-model="search" aria-label="Search tools" placeholder="Search tools and actions…" />
                <p v-if="loading" role="status" class="text-muted-foreground text-sm">Loading tools…</p>
                <div v-if="error" role="alert" class="space-y-2 text-sm">
                    <p>{{ error }}</p><Button type="button" variant="outline" size="sm" @click="load">Retry</Button>
                </div>
                <div class="space-y-3">
                    <Card v-for="row in visibleBindings" :key="row.id">
                        <CardContent class="space-y-3 p-4">
                            <div class="flex items-center gap-3">
                                <img v-if="row.imageUrl" :src="row.imageUrl" alt="" class="size-10 object-contain" />
                                <div class="min-w-0 flex-1"><p class="font-medium">{{ row.displayName }}</p><p class="text-muted-foreground text-xs">{{ row.layer }} connection</p></div>
                                <Button type="button" size="sm" :disabled="has(row.toolType)" :aria-label="`Add ${row.displayName} toolset`" @click="add(row.toolType)">{{ has(row.toolType) ? 'Added' : 'Add toolset' }}</Button>
                            </div>
                            <div class="space-y-3">
                                <Button type="button" size="sm" variant="outline" :disabled="pending[row.id]" @click="loadCatalog(row)">{{ pending[row.id] ? 'Loading actions…' : catalogErrors[row.id] ? 'Retry actions' : catalogs[row.id] ? 'Refresh actions' : 'Choose actions' }}</Button>
                                <p v-if="catalogErrors[row.id]" role="alert" class="text-sm">{{ catalogErrors[row.id] }}</p>
                                <Button v-if="catalogErrors[row.id]" as-child size="sm" variant="outline"><RouterLink :to="{ path: '/tools', query: { reconnect: row.id } }">Manage connection</RouterLink></Button>
                                <ul v-if="catalogs[row.id]" class="space-y-2">
                                    <li v-for="tool in catalogs[row.id].filter(tool => matches(`${tool.name} ${tool.summary}`))" :key="tool.name" class="flex items-start gap-3">
                                        <div class="min-w-0 flex-1"><p class="break-all text-sm font-medium">{{ tool.name }}</p><p class="text-muted-foreground text-xs">{{ tool.summary }}</p></div>
                                        <Button type="button" size="sm" variant="outline" :disabled="has(tool.name) || has(row.toolType)" :aria-label="`Add ${tool.name}`" @click="add(tool.name)">{{ has(tool.name) || has(row.toolType) ? 'Included' : 'Add' }}</Button>
                                    </li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                    <p v-if="!loading && !visibleBindings.length" class="text-muted-foreground text-sm">No connected toolsets match. Connect tools on the Tools page.</p>
                    <p class="text-sm font-medium">Built-in actions</p>
                    <ul class="space-y-3">
                        <li v-for="tool in visibleBuiltIns" :key="tool.name" class="flex items-start gap-3">
                            <div class="min-w-0 flex-1"><p class="text-sm font-medium">{{ tool.name }}</p><p class="text-muted-foreground text-xs">{{ tool.summary }}</p><p class="text-muted-foreground text-xs">{{ tool.effect }}{{ tool.gate ? ' · Requires an approval gate' : '' }}</p></div>
                            <Button type="button" size="sm" variant="outline" :disabled="has(tool.name)" :aria-label="`Add ${tool.name}`" @click="add(tool.name, tool.requires)">{{ has(tool.name) ? 'Added' : 'Add' }}</Button>
                        </li>
                    </ul>
                    <p v-if="!loading && !visibleBuiltIns.length" class="text-muted-foreground text-sm">No built-in actions match.</p>
                </div>
                <Button type="button" @click="open = false">Done</Button>
            </DialogContent>
    </fieldset>
    </Dialog>
</template>
